import { ReactNode, useState } from 'react'
import PageShell from '@/components/Common/PageShell'
import { DateRangePicker } from '@/components/DateRangePicker'
import DriveStatisticsTable from '@/components/DriveStatisticsTable'
import { requirePermission } from '@/utils/auth'
import { parseAndValidateDates } from '@/utils/datetime'
import { formatDateForTimestamp, formatDateForUrl } from '@/utils/datetime'
import { Accordion, Text } from '@mantine/core'
import { useLocalStorage } from '@mantine/hooks'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { TbCalendarTime } from 'react-icons/tb'
import dayjs from 'dayjs'
import { getDetailedDriveStatisticsOptions } from '@/api/main'
import { useSuspenseQuery } from '@tanstack/react-query'
import ErrorState from '@/components/Common/ErrorState'
import { QueryBoundary } from '@/components/Common/QueryBoundary'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'

export const Route = createFileRoute('/drive-statistics/$startDate/$endDate')({
  component: () => (
    <QueryBoundary
      loadingFallback={<DriveLoadingFallback />}
      errorTitle={i18next.t('failedToLoadDriveStatistics', { ns: 'statistics' })}
      errorMessage={i18next.t('unableToLoad', { ns: 'statistics' })}
      pageTitle={i18next.t('driveStatistics', { ns: 'statistics' })}
      fullWidth
      backButtonHref={ROUTE_PATH}
    >
      <RouteComponent />
    </QueryBoundary>
  ),
  beforeLoad: () => requirePermission('drive_statistics'),
  errorComponent: ({ error }) => (
    <PageShell title={i18next.t('errorLoadingDriveStatistics', { ns: 'statistics' })} fullWidth>
      <ErrorState
        title={i18next.t('failedToLoadDriveStatistics', { ns: 'statistics' })}
        message={error.message || i18next.t('unableToLoad', { ns: 'statistics' })}
      />
    </PageShell>
  ),
})

const ROUTE_PATH = '/drive-statistics/$startDate/$endDate'

function DriveStatisticsLayout({
  children,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onRangeChange,
  onRangeSelectionStateChange,
}: {
  children: ReactNode
  startDate: Date
  endDate: Date
  onStartDateChange: (date: Date | null) => void
  onEndDateChange: (date: Date | null) => void
  onRangeChange: (startDate: Date | null, endDate: Date | null) => void
  onRangeSelectionStateChange?: (isRangeSelectionInProgress: boolean) => void
}) {
  const { t } = useTranslation('statistics')
  const [accordionValue, setAccordionValue] = useLocalStorage<string | null>({
    key: 'dateRangePickerAccordionOpen',
    defaultValue: null,
    getInitialValueInEffect: false,
  })

  return (
    <PageShell title={t('driveStatistics')} fullWidth>
      <Accordion variant="filled" radius="md" mb="sm" value={accordionValue} onChange={setAccordionValue}>
        <Accordion.Item value="dateRange">
          <Accordion.Control icon={<TbCalendarTime size={22} color="var(--mantine-color-dimmed)" />}>
            {t('selectDateRange', { ns: 'common' })}
          </Accordion.Control>
          <Accordion.Panel>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={onStartDateChange}
              onEndDateChange={onEndDateChange}
              onRangeChange={onRangeChange}
              onRangeSelectionStateChange={onRangeSelectionStateChange}
              mb="xl"
            />
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
      {children}
    </PageShell>
  )
}

function RouteComponent() {
  const navigate = useNavigate()
  const { t } = useTranslation(['statistics', 'common'])
  const [isRangeSelectionInProgress, setIsRangeSelectionInProgress] = useState(false)

  const { startDate: startDateParam, endDate: endDateParam } = Route.useParams()
  const { startDate, endDate } = parseAndValidateDates(startDateParam, endDateParam)

  const { data: driveStatistics } = useSuspenseQuery(
    getDetailedDriveStatisticsOptions({
      path: {
        timestamp_from: formatDateForTimestamp(startDate),
        timestamp_to: formatDateForTimestamp(dayjs(endDate).add(1, 'day').toDate()),
      },
    })
  )

  const navigateToDateRange = (newStartDate: Date, newEndDate: Date) => {
    navigate({
      to: ROUTE_PATH,
      params: {
        startDate: formatDateForUrl(newStartDate),
        endDate: formatDateForUrl(newEndDate),
      },
    })
  }

  const setStartDate = (date: Date | null) => {
    if (date) {
      if (dayjs(date).isAfter(endDate)) {
        const newEndDate = dayjs(date).add(1, 'day').toDate()
        navigateToDateRange(date, newEndDate)
      } else {
        navigateToDateRange(date, endDate)
      }
    }
  }

  const setEndDate = (date: Date | null) => {
    if (date) {
      if (dayjs(date).isBefore(startDate)) {
        const newStartDate = dayjs(date).subtract(1, 'day').toDate()
        navigateToDateRange(newStartDate, date)
      } else {
        navigateToDateRange(startDate, date)
      }
    }
  }

  return (
    <DriveStatisticsLayout
      startDate={startDate}
      endDate={endDate}
      onStartDateChange={setStartDate}
      onEndDateChange={setEndDate}
      onRangeChange={(startDate, endDate) => {
        if (startDate && endDate) {
          navigateToDateRange(startDate, endDate)
        }
      }}
      onRangeSelectionStateChange={setIsRangeSelectionInProgress}
    >
      {isRangeSelectionInProgress ? (
        <Text ta="center" c="dimmed" py="xl" fz="xl" fw={600}>
          {t('selectRangeToSeeData', { ns: 'common' })}
        </Text>
      ) : (
        <DriveStatisticsTable data={driveStatistics || []} isLoading={false} error={null} />
      )}
    </DriveStatisticsLayout>
  )
}

function DriveLoadingFallback() {
  const { startDate: startDateParam, endDate: endDateParam } = Route.useParams()
  const { startDate, endDate } = parseAndValidateDates(startDateParam, endDateParam)

  return (
    <DriveStatisticsLayout
      startDate={startDate}
      endDate={endDate}
      onStartDateChange={() => {}}
      onEndDateChange={() => {}}
      onRangeChange={() => {}}
      onRangeSelectionStateChange={() => {}}
    >
      <DriveStatisticsTable data={[]} isLoading={true} error={null} />
    </DriveStatisticsLayout>
  )
}
